import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface VideoInfo {
  id: string;
  filename: string;
  size: number;
  isSegmented: boolean;
  segmentCount?: number;
  totalSize?: number;
  videoId?: string;
}

const HlsManager: React.FC = () => {
  const navigate = useNavigate();
  const [videos, setVideos] = useState<VideoInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // 获取视频列表
  const fetchVideos = async () => {
    try {
      setIsLoading(true);
      const response = await fetch('/api/videos');
      if (response.ok) {
        const data = await response.json();
        setVideos(data.map((v: any) => ({
          id: v.id,
          filename: v.filename,
          size: v.size,
          isSegmented: v.isSegmented || false,
          videoId: v.videoId,
          segmentCount: v.segmentCount,
          totalSize: v.totalSize
        })));
      }
    } catch (err) {
      console.error('获取视频列表失败:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  // 触发分片
  const handleSegment = async (videoId: string) => {
    try {
      setProcessingId(videoId);
      setMessage('正在分片...');
      
      const response = await fetch(`/api/hls/segment/${videoId}`, {
        method: 'POST'
      });
      
      const data = await response.json();
      
      if (data.isSegmented) {
        setMessage('视频已分片完成');
      } else {
        setMessage('分片任务已启动，请稍后刷新查看');
      }
      
      // 刷新列表
      setTimeout(() => fetchVideos(), 2000);
      
    } catch (err) {
      setMessage('分片失败: ' + (err as Error).message);
    } finally {
      setProcessingId(null);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  // 批量分片
  const handleBatchSegment = async () => {
    try {
      setMessage('批量分片任务已启动，请稍后刷新查看');
      
      await fetch('/api/hls/batch-segment', {
        method: 'POST'
      });
      
      // 刷新列表
      setTimeout(() => fetchVideos(), 5000);
      
    } catch (err) {
      setMessage('批量分片失败: ' + (err as Error).message);
    }
  };

  // 格式化文件大小
  const formatSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* 头部 */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">🎬 HLS 视频分片管理</h1>
            <p className="text-gray-400 mt-2">将大视频切分为 10 秒片段，实现边下边播</p>
          </div>
          <div className="flex gap-4">
            <button
              onClick={handleBatchSegment}
              className="bg-green-600 hover:bg-green-700 px-4 py-2 rounded-lg font-medium transition-colors"
            >
              🚀 批量分片所有视频
            </button>
            <button
              onClick={() => navigate('/admin')}
              className="bg-gray-600 hover:bg-gray-700 px-4 py-2 rounded-lg font-medium transition-colors"
            >
              ← 返回管理后台
            </button>
          </div>
        </div>

        {/* 消息提示 */}
        {message && (
          <div className="mb-6 p-4 bg-blue-900/50 border border-blue-500 rounded-lg">
            {message}
          </div>
        )}

        {/* 统计信息 */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-gray-800 p-4 rounded-lg">
            <div className="text-2xl font-bold">{videos.length}</div>
            <div className="text-gray-400">视频总数</div>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg">
            <div className="text-2xl font-bold text-green-400">
              {videos.filter(v => v.isSegmented).length}
            </div>
            <div className="text-gray-400">已分片</div>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg">
            <div className="text-2xl font-bold text-yellow-400">
              {videos.filter(v => !v.isSegmented).length}
            </div>
            <div className="text-gray-400">未分片</div>
          </div>
          <div className="bg-gray-800 p-4 rounded-lg">
            <div className="text-2xl font-bold text-blue-400">
              {formatSize(videos.reduce((sum, v) => sum + v.size, 0))}
            </div>
            <div className="text-gray-400">总大小</div>
          </div>
        </div>

        {/* 视频列表 */}
        {isLoading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-white/30 border-t-white rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-400">加载中...</p>
          </div>
        ) : (
          <div className="bg-gray-800 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-4 text-left">文件名</th>
                  <th className="px-6 py-4 text-left">大小</th>
                  <th className="px-6 py-4 text-left">分片状态</th>
                  <th className="px-6 py-4 text-left">片段数</th>
                  <th className="px-6 py-4 text-left">操作</th>
                </tr>
              </thead>
              <tbody>
                {videos.map((video) => (
                  <tr key={video.id} className="border-t border-gray-700 hover:bg-gray-700/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">🎥</span>
                        <div>
                          <div className="font-medium">{video.filename}</div>
                          <div className="text-sm text-gray-400">ID: {video.videoId?.substring(0, 8)}...</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-gray-300">{formatSize(video.size)}</td>
                    <td className="px-6 py-4">
                      {video.isSegmented ? (
                        <span className="inline-flex items-center gap-2 px-3 py-1 bg-green-900/50 text-green-400 rounded-full text-sm">
                          ✅ 已分片
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-2 px-3 py-1 bg-yellow-900/50 text-yellow-400 rounded-full text-sm">
                          ⏳ 未分片
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-gray-300">
                      {video.segmentCount || '-'}
                    </td>
                    <td className="px-6 py-4">
                      {video.isSegmented ? (
                        <button
                          onClick={() => handleSegment(video.videoId!)}
                          disabled={processingId === video.videoId}
                          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 rounded text-sm transition-colors"
                        >
                          {processingId === video.videoId ? '处理中...' : '重新分片'}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleSegment(video.videoId!)}
                          disabled={processingId === video.videoId}
                          className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 rounded text-sm transition-colors"
                        >
                          {processingId === video.videoId ? '处理中...' : '开始分片'}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            
            {videos.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <div className="text-4xl mb-4">📁</div>
                <p>暂无视频</p>
              </div>
            )}
          </div>
        )}

        {/* 说明 */}
        <div className="mt-8 p-6 bg-gray-800 rounded-lg">
          <h3 className="text-lg font-semibold mb-4">📖 HLS 分片说明</h3>
          <ul className="space-y-2 text-gray-300">
            <li>• <strong>什么是 HLS？</strong> HTTP Live Streaming，苹果开发的流媒体协议，将视频切分为小片段</li>
            <li>• <strong>分片大小：</strong> 每个片段 10 秒，可根据网络状况动态调整清晰度</li>
            <li>• <strong>自动分片：</strong> 上传大于 50MB 的视频会自动触发分片</li>
            <li>• <strong>边下边播：</strong> 分片后播放器只需加载当前片段，大幅提升加载速度</li>
            <li>• <strong>缓存优化：</strong> 片段文件可长期缓存，重复播放时直接从缓存读取</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default HlsManager;
